import { useEffect, useState } from 'react'

import { Url } from 'next/dist/shared/lib/router/router'
import { useRouter } from 'next/router'

import { IS_PROD_LIKE_ENVIRONMENT } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useIsTrialPeriod } from '@/hooks/billing/useIsTrialPeriod'
import { useHandleError } from '@/hooks/common/useErrorHandler'

export const useSafePush = () => {
  const { handleError: _handleError } = useHandleError()
  const { trialExpired } = useIsTrialPeriod()

  const [onChanging, setOnChanging] = useState(false)

  const handleRouteChange = () => {
    setOnChanging(false)
  }
  const router = useRouter()
  // safePush is used to avoid route pushing errors when users click multiple times or when the network is slow
  // (e.g. "Error: Abort fetching component for route")
  const safePush = (
    path: string,
    as?: Url,
    options?: { shallow: boolean },
    handleError: boolean = true,
  ) => {
    if (onChanging) {
      return
    }
    setOnChanging(true)
    const routerPushPromise = new Promise<void>((resolve, reject) => {
      // TODO: removing this prefetching for now for testing
      // purposes (Andrew 04/04/2025)
      // router.prefetch(path, path, { priority: true })
      router
        .push(path, as, options)
        .catch((reason) => {
          reject(`Navigation failed: ${reason}`)
        })
        .finally(() => {
          if (window.location.href.endsWith(path)) {
            resolve()
          } else if (!router.asPath.startsWith('/upgrade')) {
            // don't show error if user was navigated to upgrade page;
            // TODO: this is a bit hacky since it relies on /upgrade being in the URL
            reject('Navigation incomplete but did not time out.')
          }
        })
    })

    // 8 second navigation timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(
        () => reject('Navigation timed out.'),
        IS_PROD_LIKE_ENVIRONMENT ? 8000 : 20000,
      )
    })

    Promise.race([routerPushPromise, timeoutPromise])
      .catch((reason) => {
        if (router.asPath.startsWith('/upgrade') && trialExpired) {
          // don't show error if user was navigated to upgrade page;
          // TODO: this is a bit hacky since it relies on /upgrade being in the URL
          return
        }
        if (handleError) {
          window.location.href = path
          _handleError(
            new SweetspotError({
              name: 'NAVIGATION_ERROR',
              message: `Navigation to ${path} failed from ${router.asPath}: ${reason}`,
            }),
            true,
          )
        }
      })
      .finally(() => {
        setOnChanging(false)
      })

    // TODO: remove this but keeping it here for now incase we need to revert (08/08/2024)
    // router
    //   .push(path, as, options)
    //   .catch((reason: any) => {
    //     setOnChanging(false)
    //     if (handleError) {
    //       window.location.href = path
    //       _handleError(
    //         {
    //           name: 'RouteChangeError',
    //           stack: `Navigation to ${path} failed from ${router.pathname}. ${reason}`,
    //         },
    //         true
    //       )
    //     }
    //   })
    //   .finally(() => {
    //     setOnChanging(false)
    //     if (handleError && !window.location.href.endsWith(path)) {
    //       window.location.href = path
    //       _handleError(
    //         {
    //           name: 'RouteChangeError',
    //           stack: `Navigation to ${path} failed from ${router.pathname}.`,
    //         },
    //         true
    //       )
    //     }
    //   })
  }

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange)
    router.events.on('routeChangeError', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
      router.events.off('routeChangeError', handleRouteChange)
    }
  }, [router, setOnChanging])
  return { safePush }
}
